// frontend/src/EducationDashboard.js
// 教育進捗ダッシュボード (リニューアル版)
//
// 大枠:
//   1. ヘッダー (タイトル / 戻る / デモ)
//   2. 分析条件フィルタ (年月 / 術式レベル / 必須区分 / 診療科 / 術式 / 年次 / 個人)
//   3. 可視化セクション × 3 (以下は後続ステップで実装)
//      a. 直近の自立者推移
//      b. 指導した人の過去配置状況
//      c. 育成された人の過去配置状況
//
// このコミットではフィルタと大枠までを実装。 可視化本体は空プレースホルダ。

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FlaskConical, TrendingUp, Users, UserCheck, Search, Clock, AlertTriangle } from "lucide-react";
import Header from "./components/ui/Header";
import { useOrganization } from "./contexts/OrganizationContext";
import { supabase, getStaffList } from "./supabase";
import EducationDashboardFilters, {
  makeDefaultFilters,
} from "./components/EducationDashboardFilters";
import IndependenceTrendSection from "./components/IndependenceTrendSection";
import InstructorHistorySection from "./components/InstructorHistorySection";
import TraineeHistorySection from "./components/TraineeHistorySection";

const PAGE_SIZE = 1000;

// procedures を全件取得 (Supabase 1 クエリ上限は 1000 件なのでページネーション)
async function fetchAllProcedures(organizationId) {
  const all = [];
  let page = 0;
  while (true) {
    const { data, error } = await supabase
      .from("procedures")
      .select("id, name, department_id, required_roles, level_id")
      .eq("organization_id", organizationId)
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE_SIZE) break;
    page += 1;
  }
  return all;
}

// 指定テーブルの日付カラムから YYYY-MM の distinct 集合を取得 (ページネーション)
async function collectMonthsFromTable(table, dateColumn, organizationId) {
  const monthSet = new Set();
  let page = 0;
  while (true) {
    const { data, error } = await supabase
      .from(table)
      .select(dateColumn)
      .eq("organization_id", organizationId)
      .not(dateColumn, "is", null)
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    data.forEach((row) => {
      const d = row[dateColumn];
      if (d) monthSet.add(String(d).slice(0, 7));
    });
    if (data.length < PAGE_SIZE) break;
    page += 1;
  }
  return monthSet;
}

// skill_records: level='独り立ち'/'指導可' のレコードから自立日の月を集める
//   - evaluation_history の 独り立ち エントリ + created_at の新しい方
async function collectMonthsFromSkillHistory(organizationId) {
  const monthSet = new Set();
  let page = 0;
  while (true) {
    const { data, error } = await supabase
      .from("skill_records")
      .select("evaluation_history, level, created_at")
      .eq("organization_id", organizationId)
      .in("level", ["独り立ち", "指導可"])
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    data.forEach((row) => {
      const hist = Array.isArray(row.evaluation_history) ? row.evaluation_history : [];
      let histDate = null;
      hist.forEach((h) => {
        if (!h || h.level !== "独り立ち") return;
        const d = h.evaluated_at || h.date;
        if (d && (!histDate || String(d).localeCompare(histDate) < 0)) histDate = String(d);
      });
      const createdDate = row.created_at ? String(row.created_at).slice(0, 10) : null;
      let d;
      if (histDate && createdDate) d = histDate.localeCompare(createdDate) > 0 ? histDate : createdDate;
      else d = histDate || createdDate;
      if (d) monthSet.add(d.slice(0, 7));
    });
    if (data.length < PAGE_SIZE) break;
    page += 1;
  }
  return monthSet;
}

// データのある月を 3 ソースから集めて新しい順にソート
async function fetchAvailableMonths(organizationId) {
  const [history, assignments] = await Promise.all([
    collectMonthsFromSkillHistory(organizationId),
    collectMonthsFromTable("staff_assignments", "assignment_date", organizationId),
  ]);
  const union = new Set([...history, ...assignments]);
  return [...union].sort((x, y) => y.localeCompare(x));
}

// filter の中身が等価か判定 (Set 含む)
function filtersEqual(a, b) {
  if (!a || !b) return false;
  if (a.dateFrom !== b.dateFrom || a.dateTo !== b.dateTo) return false;
  if (a.requirement !== b.requirement) return false;
  const sameSet = (s1, s2) => {
    if (!s1 || !s2) return s1 === s2;
    if (s1.size !== s2.size) return false;
    for (const v of s1) if (!s2.has(v)) return false;
    return true;
  };
  return (
    sameSet(a.levelIds, b.levelIds) &&
    sameSet(a.requirements, b.requirements) &&
    sameSet(a.roles, b.roles) &&
    sameSet(a.deptIds, b.deptIds) &&
    sameSet(a.procedureIds, b.procedureIds) &&
    sameSet(a.years, b.years) &&
    sameSet(a.staffIds, b.staffIds)
  );
}

export default function EducationDashboard() {
  const { currentOrganization } = useOrganization();

  // ========= Master data =========
  const [departments, setDepartments] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [procedureLevels, setProcedureLevels] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]); // "YYYY-MM" 配列、新しい順
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ========= Filter state =========
  // filters: 編集中 / appliedFilters: 「検索」ボタン押下で確定して可視化に渡される
  // searchCount: 検索を押した回数。 0 = 未検索、各セクションはこのカウントで初回 fetch を遅延する。
  const [filters, setFilters] = useState(() => makeDefaultFilters([]));
  const [appliedFilters, setAppliedFilters] = useState(() => makeDefaultFilters([]));
  const [searchCount, setSearchCount] = useState(0);

  // Master data の fetch
  useEffect(() => {
    const run = async () => {
      if (!currentOrganization?.id) return;
      setLoading(true);
      setError("");
      try {
        const orgId = currentOrganization.id;
        const [deptRes, procAll, levelRes, jobTypeRes, staffAll] = await Promise.all([
          supabase
            .from("departments")
            .select("id, name")
            .eq("organization_id", orgId)
            .order("name"),
          fetchAllProcedures(orgId),
          supabase
            .from("procedure_levels")
            .select("id, name, color, display_order")
            .eq("organization_id", orgId)
            .order("display_order"),
          supabase
            .from("job_types")
            .select("id, has_skill_evaluation")
            .eq("organization_id", orgId),
          getStaffList(orgId),
        ]);
        if (deptRes.error) throw deptRes.error;
        if (levelRes.error) throw levelRes.error;
        if (jobTypeRes.error) throw jobTypeRes.error;

        const skillJobIds = new Set(
          (jobTypeRes.data || [])
            .filter((j) => j.has_skill_evaluation === true)
            .map((j) => j.id)
        );
        const filteredStaff = (staffAll || [])
          .filter((s) => s.is_active !== false)
          .filter((s) => skillJobIds.has(s.job_type_id));

        setDepartments(deptRes.data || []);
        setProcedures(
          (procAll || []).map((p) => ({
            ...p,
            required_roles:
              typeof p.required_roles === "string"
                ? JSON.parse(p.required_roles || "[]")
                : p.required_roles || [],
          }))
        );
        setProcedureLevels(levelRes.data || []);
        setStaffList(filteredStaff);

        // 初期フィルタ: procedureLevels を全選択に更新
        const nextLevelIds = new Set((levelRes.data || []).map((l) => l.id));
        setFilters((prev) => ({ ...prev, levelIds: new Set(nextLevelIds) }));
        setAppliedFilters((prev) => ({ ...prev, levelIds: new Set(nextLevelIds) }));

        // データがある月を列挙 (recognized_date + assignment_date)
        const months = await fetchAvailableMonths(orgId);
        setAvailableMonths(months);
        // 初期の年月 (dateFrom/dateTo) をデータ範囲に合わせる
        if (months.length > 0) {
          const dateTo = months[0];               // 一番新しい
          const idx12 = Math.min(months.length - 1, 11);
          const dateFrom = months[idx12];         // 新しい方から 12 ヶ月目
          setFilters((prev) => ({ ...prev, dateFrom, dateTo }));
          setAppliedFilters((prev) => ({ ...prev, dateFrom, dateTo }));
        }
      } catch (e) {
        console.error(e);
        setError(e.message || "データ取得に失敗しました");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [currentOrganization?.id]);

  const yearOptions = useMemo(() => {
    const ys = [...new Set(staffList.map((s) => Number(s.year)).filter((y) => !isNaN(y)))];
    return ys.sort((a, b) => a - b);
  }, [staffList]);

  if (!currentOrganization?.id) {
    return (
      <div style={styles.container}>
        <Header />
        <div style={styles.content}>
          <div style={styles.noData}>組織を選択してください</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Header />
      <div style={styles.content}>
        {/* ===== Page header ===== */}
        <div style={styles.pageHeader}>
          <Link to="/home" style={styles.backLink}>
            <ArrowLeft size={14} /> ホームへ戻る
          </Link>
          <h1 style={styles.title}>教育進捗ダッシュボード</h1>
          <p style={styles.subtitle}>
            自立者推移・指導者/被指導者の配置状況を分析します。
            <Link to="/education-dashboard-demo" style={styles.demoLink}>
              <FlaskConical size={12} /> デモデータで機能を試す
            </Link>
          </p>
        </div>

        {error && (
          <div style={{ ...styles.errorBox, display: "flex", alignItems: "center", gap: 6 }}>
            <AlertTriangle size={14} /> {error}
          </div>
        )}
        {loading && <div style={styles.loadingBox}>データを読み込み中...</div>}

        {/* ===== Filter panel + 検索ボタン ===== */}
        <EducationDashboardFilters
          filters={filters}
          onChange={setFilters}
          departments={departments}
          procedures={procedures}
          procedureLevels={procedureLevels}
          staffList={staffList}
          yearOptions={yearOptions}
          availableMonths={availableMonths}
        />
        {(() => {
          const dirty = !filtersEqual(filters, appliedFilters);
          return (
            <div style={styles.searchBar}>
              <div style={{ fontSize: 12, color: dirty ? "#b45309" : "#64748b", display: "flex", alignItems: "center", gap: 6 }}>
                {dirty ? (
                  <><Clock size={12} /> 未適用の変更があります。 検索を実行して可視化に反映してください。</>
                ) : (
                  <>可視化は現在のフィルタ条件で表示中です。</>
                )}
              </div>
              <button
                onClick={() => {
                  setAppliedFilters({
                    ...filters,
                    levelIds: new Set(filters.levelIds),
                    requirements: new Set(filters.requirements),
                    roles: new Set(filters.roles),
                    deptIds: new Set(filters.deptIds),
                    procedureIds: new Set(filters.procedureIds),
                    years: new Set(filters.years),
                    staffIds: new Set(filters.staffIds),
                  });
                  setSearchCount((c) => c + 1);
                }}
                style={{
                  ...styles.searchBtn,
                  background: "#1d4ed8",
                  cursor: "pointer",
                }}
              >
                <Search size={14} /> 検索を実行
              </button>
            </div>
          );
        })()}

        {/* ================================================================= */}
        {/* ===== グループ A: 直近の自立者推移 ===== */}
        {/* ================================================================= */}
        <div style={styles.groupHeader}>
          <div style={styles.groupBar} />
          <h2 style={styles.groupTitle}>直近の自立者推移</h2>
        </div>

        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.sectionIcon}><TrendingUp size={18} /></div>
            <div>
              <h3 style={styles.sectionTitle}>術式別 自立者推移</h3>
              <p style={styles.sectionSubtitle}>
                月別に自立認定数を集計し、術式ごとに積み上げ表示します。 棒や凡例をクリックすると下の一覧がその術式に絞り込まれます。
              </p>
            </div>
          </div>
          <IndependenceTrendSection
            organizationId={currentOrganization.id}
            filters={appliedFilters}
            departments={departments}
            procedures={procedures}
            staffList={staffList}
            searchCount={searchCount}
          />
        </section>

        {/* ================================================================= */}
        {/* ===== グループ B: 過去配置分析 ===== */}
        {/* ================================================================= */}
        <div style={styles.groupHeader}>
          <div style={{ ...styles.groupBar, background: "#8b5cf6" }} />
          <h2 style={styles.groupTitle}>過去配置分析</h2>
        </div>

        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={{ ...styles.sectionIcon, background: "linear-gradient(135deg, #8b5cf6, #7c3aed)" }}>
              <UserCheck size={18} />
            </div>
            <div>
              <h3 style={styles.sectionTitle}>指導した人の過去配置</h3>
              <p style={styles.sectionSubtitle}>
                指導者として配置された記録を一覧 / 術式別 / 個人別で可視化します。 一覧の術式・指導者名をクリックすると下の該当セクションが絞り込まれます。
              </p>
            </div>
          </div>
          <InstructorHistorySection
            organizationId={currentOrganization.id}
            filters={appliedFilters}
            departments={departments}
            procedures={procedures}
            staffList={staffList}
            searchCount={searchCount}
          />
        </section>

        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={{ ...styles.sectionIcon, background: "linear-gradient(135deg, #ec4899, #be185d)" }}>
              <Users size={18} />
            </div>
            <div>
              <h3 style={styles.sectionTitle}>育成された人の過去配置</h3>
              <p style={styles.sectionSubtitle}>
                被指導者 (育成を受けた側) として配置された記録を一覧 / 術式別 / 個人別で可視化します。 一覧の術式・育成者名クリックで下の該当セクションが絞り込まれます。
              </p>
            </div>
          </div>
          <TraineeHistorySection
            organizationId={currentOrganization.id}
            filters={appliedFilters}
            departments={departments}
            procedures={procedures}
            staffList={staffList}
            searchCount={searchCount}
          />
        </section>

        <DebugFiltersCard filters={appliedFilters} />
      </div>
    </div>
  );
}

// =============================================================================
// デバッグ: 現在のフィルタ内容を可視化 (開発中のみ)
// =============================================================================
function DebugFiltersCard({ filters }) {
  const summary = {
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    levelIds: [...filters.levelIds],
    requirements: [...filters.requirements],
    deptIds: [...filters.deptIds],
    procedureIds: [...filters.procedureIds],
    years: [...filters.years],
    staffIds: [...filters.staffIds],
  };
  return (
    <details style={styles.debugCard}>
      <summary style={styles.debugSummary}>
        <Search size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
        現在のフィルタ内容 (デバッグ用)
      </summary>
      <pre style={styles.debugPre}>{JSON.stringify(summary, null, 2)}</pre>
    </details>
  );
}

// =============================================================================
// styles
// =============================================================================
const styles = {
  container: { minHeight: "100vh", background: "#f8fafc" },
  content: { maxWidth: 1400, margin: "0 auto", padding: 24 },
  pageHeader: { marginBottom: 20 },
  backLink: {
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 12, color: "#1d4ed8", textDecoration: "none", marginBottom: 6,
  },
  title: { fontSize: 24, fontWeight: 700, color: "#0f172a", margin: "4px 0 6px" },
  subtitle: { fontSize: 13, color: "#64748b", margin: 0, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  demoLink: {
    fontSize: 11, color: "#1d4ed8", textDecoration: "none",
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "2px 8px", background: "#eff6ff", borderRadius: 10,
  },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
    padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 12,
  },
  loadingBox: {
    background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8",
    padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 12,
  },
  noData: {
    padding: 60, textAlign: "center", color: "#94a3b8", fontSize: 14,
    background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
  },
  section: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 20,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
  sectionHeader: {
    display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14,
    paddingBottom: 10, borderBottom: "1px solid #f1f5f9",
  },
  sectionIcon: {
    width: 36, height: 36, borderRadius: 10,
    background: "linear-gradient(135deg, #3b82f6, #2563eb)",
    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: "#0f172a", margin: 0 },
  sectionSubtitle: { fontSize: 11, color: "#64748b", margin: "2px 0 0" },
  sectionBody: { minHeight: 160 },
  placeholder: {
    minHeight: 140,
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    background: "#f8fafc", borderRadius: 10, border: "1px dashed #cbd5e1",
  },
  debugCard: {
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: "8px 12px", marginTop: 16, fontSize: 11,
  },
  debugSummary: {
    cursor: "pointer", color: "#64748b", fontWeight: 600, userSelect: "none",
  },
  debugPre: {
    marginTop: 8, padding: 10, background: "#0f172a", color: "#4ade80",
    borderRadius: 6, fontSize: 10, overflow: "auto",
  },
  searchBar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 10, flexWrap: "wrap",
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: "10px 14px", marginBottom: 16,
  },
  searchBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "8px 18px", borderRadius: 8, border: "none",
    color: "#fff", fontWeight: 700, fontSize: 13,
  },
  groupHeader: {
    display: "flex", alignItems: "center", gap: 10,
    margin: "24px 0 10px", padding: "0 2px",
  },
  groupBar: {
    width: 4, height: 22, borderRadius: 2, background: "#3b82f6",
  },
  groupTitle: {
    fontSize: 18, fontWeight: 700, color: "#0f172a", margin: 0,
  },
};
